import { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import ptBR from 'date-fns/locale/pt-BR';
import enUS from 'date-fns/locale/en-US';

registerLocale('es', es);
registerLocale('pt-BR', ptBR);
registerLocale('en-US', enUS);

// Same default as the app (utils/constants): Spanish when nothing else applies.
const DEFAULT_LANGUAGE = 'es';

const monthNamesOf = (localeData) =>
  Array.from({ length: 12 }, (_, month) => localeData.localize.month(month));

const PICKER_CONFIG = {
  es: { locale: 'es', dateFormat: 'dd/MM/yyyy', monthNames: monthNamesOf(es) },
  pt: {
    locale: 'pt-BR',
    dateFormat: 'dd/MM/yyyy',
    monthNames: monthNamesOf(ptBR),
  },
  en: {
    locale: 'en-US',
    dateFormat: 'MM/dd/yyyy',
    monthNames: monthNamesOf(enUS),
  },
};

// NAWI only assesses people aged 18 to 100 inclusive (guide v1.9); the backend
// enforces the same rule on the current date.
const MINIMUM_AGE = 18;
const MAXIMUM_AGE = 100;

// Local midnight today; toISOString() would use the UTC day instead.
export const getLocalToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

// The calendar day `years` before today; February 29 falls back to February
// 28 on non-leap years, like the backend's month/day comparison.
const sameDayYearsAgo = (years) => {
  const today = getLocalToday();
  const year = today.getFullYear() - years;
  const lastDayOfMonth = new Date(year, today.getMonth() + 1, 0).getDate();
  return new Date(
    year,
    today.getMonth(),
    Math.min(today.getDate(), lastDayOfMonth),
  );
};

// Latest allowed birth date: whoever turns 18 today.
export const getDobMaxDate = () => sameDayYearsAgo(MINIMUM_AGE);

// Earliest allowed birth date: the day after whoever turns 101 today.
export const getDobMinDate = () => {
  const turned101 = sameDayYearsAgo(MAXIMUM_AGE + 1);
  return new Date(
    turned101.getFullYear(),
    turned101.getMonth(),
    turned101.getDate() + 1,
  );
};

// Newest first: a date of birth is usually reached by scrolling down from
// the newest allowed year.
export const getDobYears = () => {
  const lastYear = getDobMaxDate().getFullYear();
  const firstYear = getDobMinDate().getFullYear();
  return Array.from(
    { length: lastYear - firstYear + 1 },
    (_, index) => lastYear - index,
  );
};

// The dashboard language is persisted by the app and by the language selector.
export const getDobPickerConfig = () => {
  let language = null;
  try {
    language = localStorage.getItem('language');
  } catch (error) {
    language = null;
  }
  return PICKER_CONFIG[language] || PICKER_CONFIG[DEFAULT_LANGUAGE];
};

// Every supported format is two digits, two digits, four digits: the
// separators are added as the digits are typed. While deleting, a trailing
// separator is not re-added, otherwise it could never be erased.
export const maskDobInput = (text, { deleting = false } = {}) => {
  const digits = String(text || '')
    .replace(/\D/g, '')
    .slice(0, 8);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)];
  const masked = parts.filter(Boolean).join('/');
  const endsGroup = digits.length === 2 || digits.length === 4;
  return endsGroup && !deleting ? `${masked}/` : masked;
};

// Backend contract is YYYY-MM-DD built from the local date parts.
export const formatLocalIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const pad = (value, size) => String(value).padStart(size, '0');
  return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1, 2)}-${pad(
    date.getDate(),
    2,
  )}`;
};
